import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import SignaturePad from "react-signature-canvas";
import { FaChevronDown, FaChevronUp, FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import LoadingOverlay from "../../components/LoadingOverlay";
import "../../styles/Form.css";
import "../../styles/Signature.css";
import "../../styles/MyAccountPage.css";

function MyAccountPage() {
  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const sigPadRef = useRef(null);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });
  const [savedSignature, setSavedSignature] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showSignatureSection, setShowSignatureSection] = useState(true);
  const [showPasswordSection, setShowPasswordSection] = useState(true);

  useEffect(() => {
    const loadAccount = async () => {
      try {
        const { data } = await axios.get(`${BASE_URL}/api/user/account`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        setForm({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          email: data.email || "",
        });
        setSavedSignature(data.user_signature || null);
      } catch (err) {
        console.error("Failed to load account:", err);
        alert("Failed to load account details.");
      }
    };

    loadAccount();
  }, [BASE_URL]);

  const handleFormChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePasswordChange = (e) => {
    setPasswordForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const resolveSignatureToSave = () => {
    if (showSignaturePad) {
      if (sigPadRef.current?.isEmpty()) {
        return null;
      }

      return sigPadRef.current?.getCanvas().toDataURL("image/png");
    }

    return savedSignature || null;
  };

  const handleSaveAccount = async () => {
    const signatureToSave = resolveSignatureToSave();

    if (showSignaturePad && !signatureToSave) {
      alert("Please draw your signature before saving.");
      return;
    }

    try {
      setLoading(true);
      await axios.put(
        `${BASE_URL}/api/user/account`,
        {
          ...form,
          user_signature: signatureToSave,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setSavedSignature(signatureToSave);
      setShowSignaturePad(false);
      alert("My Account updated successfully.");
    } catch (err) {
      console.error("Account save failed:", err);
      alert(err?.response?.data?.message || "Failed to save My Account.");
    } finally {
      setLoading(false);
    }
  };

  const handleSavePassword = async () => {
    if (
      !passwordForm.currentPassword ||
      !passwordForm.newPassword ||
      !passwordForm.confirmPassword
    ) {
      alert("Please complete all password fields.");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert("New passwords do not match.");
      return;
    }

    try {
      setSavingPassword(true);
      await axios.put(
        `${BASE_URL}/api/user/account/password`,
        passwordForm,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      alert("Password updated successfully.");
    } catch (err) {
      console.error("Password save failed:", err);
      alert(err?.response?.data?.message || "Failed to update password.");
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <>
      {(loading || savingPassword) && (
        <LoadingOverlay
          text={loading ? "Saving account..." : "Updating password..."}
        />
      )}

      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            My Account
          </span>
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-success px-4"
            onClick={handleSaveAccount}
            disabled={loading}
          >
            Save
          </button>
          <button
            type="button"
            className="btn btn-secondary px-4"
            onClick={() => navigate(-1)}
          >
            Return
          </button>
        </div>
      </div>

      <div className="form-wrapper my-4 my-account-page">
        <div className="row align-items-center mb-4">
          <div className="col-12">
            <h3 className="mb-2">Manage Your Account Settings.</h3>
            <div>1. Edit your preferences</div>
            <div>2. Set or update your signature</div>
            <div>3. Change your password</div>
          </div>
        </div>

        <section className="my-account-section">
          <div className="my-account-section-header">
            <span className="my-account-step">1</span>
            <span>Edit Your Preferences</span>
          </div>
          <div className="my-account-section-body">
            <div className="row mb-3">
              <div className="col-md-4">
                <label>First Name</label>
                <input
                  type="text"
                  name="first_name"
                  className="form-control"
                  value={form.first_name}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="col-md-4">
                <label>Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  className="form-control"
                  value={form.last_name}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="col-md-4">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={form.email}
                  onChange={handleFormChange}
                  required
                />
              </div>
            </div>
          </div>
        </section>

        <section className="my-account-section">
          <button
            type="button"
            className="my-account-section-header light my-account-section-toggle"
            onClick={() => setShowSignatureSection((prev) => !prev)}
          >
            <span className="my-account-step">2</span>
            <span>Your Signature</span>
            <span className="my-account-toggle-icon">
              {showSignatureSection ? <FaChevronUp /> : <FaChevronDown />}
            </span>
          </button>
          {showSignatureSection && <div className="my-account-section-body">

          {!showSignaturePad && savedSignature ? (
            <div className="mt-2">
              <img
                src={savedSignature}
                alt="Saved signature"
                className="signature-image"
              />
              <div className="signature-buttons mt-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowSignaturePad(true)}
                >
                  Re-do My Signature
                </button>
              </div>
            </div>
          ) : (
            <div className="signature-container mt-2">
              <SignaturePad
                ref={sigPadRef}
                canvasProps={{ className: "sigCanvas" }}
              />
              <div className="signature-buttons mt-2">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={() => sigPadRef.current?.clear()}
                >
                  Clear Signature
                </button>
                {savedSignature && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowSignaturePad(false)}
                  >
                    Keep Existing Signature
                  </button>
                )}
              </div>
            </div>
          )}
          </div>}
        </section>

        <section className="my-account-section">
          <button
            type="button"
            className="my-account-section-header my-account-section-toggle"
            onClick={() => setShowPasswordSection((prev) => !prev)}
          >
            <span className="my-account-step">3</span>
            <span>Change Your Password</span>
            <span className="my-account-toggle-icon">
              {showPasswordSection ? <FaChevronUp /> : <FaChevronDown />}
            </span>
          </button>
          {showPasswordSection && <div className="my-account-section-body">
            <div className="my-account-password-grid">
              <div>
                <div className="mb-2">
                  <div className="input-group">
                    <input
                      type={passwordVisible ? "text" : "password"}
                      name="currentPassword"
                      className="form-control my-account-password-input"
                      value={passwordForm.currentPassword}
                      onChange={handlePasswordChange}
                      placeholder="PLEASE ENTER YOUR CURRENT PASSWORD"
                    />
                    <span
                      className="input-group-text"
                      onClick={() => setPasswordVisible((prev) => !prev)}
                      style={{ cursor: "pointer" }}
                    >
                      {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </div>
                </div>
                <div className="mb-2">
                  <input
                    type={passwordVisible ? "text" : "password"}
                    name="newPassword"
                    className="form-control my-account-password-input"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="PLEASE ENTER YOUR NEW PASSWORD"
                  />
                </div>
                <div>
                  <input
                    type={passwordVisible ? "text" : "password"}
                    name="confirmPassword"
                    className="form-control my-account-password-input"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="CONFIRM PASSWORD"
                  />
                </div>
              </div>

              <div className="my-account-password-help">
                <div className="my-account-password-help-accent">*</div>
                <div className="my-account-password-help-content">
                  <strong>Suggestions to create a strong password:</strong>
                  <ul>
                    <li>Your password must be a minimum of 9 characters in length</li>
                    <li>Avoid patterns and series of numbers and letters</li>
                    <li>Try a sentence or easily memorable series of words separated by spaces</li>
                    <li>Special characters are not required</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="my-account-password-button-row">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSavePassword}
                disabled={savingPassword}
              >
                Change Password
              </button>
            </div>
          </div>}
        </section>
      </div>
    </>
  );
}

export default MyAccountPage;
